import { useState } from "react";
import { Check, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { ActionButton } from "./kit";
import SettingsSheet from "./SettingsSheet";
import { assignDrill, withdrawDrill } from "../lib/drills";
import { joinNames } from "../lib/drillHolders";
import { coachPlayersName } from "../lib/coachPlayersName";

/**
 * "Assign <drill>" (#1159 slice 2, board CoachDrillBank, D251, D298): a kit
 * sheet (the one the New goal sheet uses) listing the coach's linked
 * players. A player who holds the drill shows "Has it" and Withdraw; the
 * others have a tick, unticked on open. One `POST /drills/assignments` per
 * ticked player (`Promise.allSettled`, #481 D2), so one can fail alone: the
 * sheet then stays open, names who failed, and keeps them ticked.
 *
 * The holders live in the bank (it draws them under each drill too), so this
 * sheet reports changes up (`onAdd`, `onWithdrawn`) instead of owning them.
 * Failures show in the sheet (D57), never as a toast, except a failed
 * Withdraw, which keeps the shipped toast words (no board draws one).
 */

const ROW = {
  display: "flex", alignItems: "center", gap: 8, minHeight: 52, boxSizing: "border-box", padding: "4px 4px 4px 14px",
  background: "var(--nw-surface)", borderRadius: "var(--nw-radius-card)",
};
const NAME = { fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 15, color: "var(--nw-ink)", minWidth: 0, overflowWrap: "anywhere" };
const CHIP = {
  display: "inline-flex", alignItems: "center", padding: "2px 8px", background: "var(--nw-line-faint)", color: "var(--nw-ink-lede)",
  fontSize: 11, fontWeight: 600, borderRadius: "var(--nw-radius-pill)", whiteSpace: "nowrap",
};
const WITHDRAW = {
  display: "inline-flex", alignItems: "center", flexShrink: 0, minHeight: 44, padding: "0 14px", background: "var(--nw-surface)",
  border: "1px solid var(--nw-line-strong)", borderRadius: "var(--nw-radius-control)", fontSize: 14, fontWeight: 600, color: "var(--nw-alert)",
  cursor: "pointer",
};

function Tick({ checked, name, onToggle, testId }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={`Assign to ${name}`}
      data-testid={testId}
      onClick={onToggle}
      style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, padding: 0, background: "none", border: 0, cursor: "pointer" }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 22, height: 22, boxSizing: "border-box", borderRadius: "var(--nw-radius-check)", display: "inline-flex", alignItems: "center", justifyContent: "center",
          ...(checked ? { background: "var(--nw-navy)" } : { background: "var(--nw-surface)", border: "2px solid var(--nw-line-strong)" }),
        }}
      >
        {checked && <Check size={14} strokeWidth={3} style={{ color: "#fff" }} />}
      </span>
    </button>
  );
}

export default function CoachDrillAssignSheet({ drill, roster, holders, onClose, onAdd, onWithdrawn, onAssigned }) {
  const [ticks, setTicks] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [withdrawing, setWithdrawing] = useState(null);
  // { failed: [name], now: [name] } after a partly failed Assign.
  const [partial, setPartial] = useState(null);
  const holderByPlayer = new Map(holders.map((h) => [h.player_id, h]));

  const toggle = (playerId) =>
    setTicks((cur) => {
      const next = new Set(cur);
      if (next.has(playerId)) next.delete(playerId);
      else next.add(playerId);
      return next;
    });

  const ticked = roster.filter((p) => ticks.has(p.player_id) && !holderByPlayer.has(p.player_id));
  const label = ticked.length === 1 ? `Assign to ${ticked[0].full_name}` : `Assign to ${ticked.length} players`;

  const submit = async () => {
    if (busy || ticked.length === 0) return;
    setBusy(true);
    const results = await Promise.allSettled(ticked.map((p) => assignDrill(p.player_id, drill.drill_id)));
    const done = [];
    const failed = [];
    ticked.forEach((p, i) => {
      const r = results[i];
      if (r.status === "fulfilled") done.push({ player_id: p.player_id, full_name: p.full_name, assignment_id: r.value.assignment_id });
      else failed.push(p);
    });
    if (done.length) onAdd(drill.drill_id, done);
    if (failed.length === 0) {
      onAssigned(`${drill.title} assigned to ${joinNames(done.map((d) => d.full_name))}.`);
      onClose();
      return;
    }
    setPartial({ failed: failed.map((p) => p.full_name), now: done.map((d) => d.full_name) });
    setTicks(new Set(failed.map((p) => p.player_id)));
    setBusy(false);
  };

  const withdraw = async (holder) => {
    if (withdrawing) return;
    setWithdrawing(holder.player_id);
    try {
      await withdrawDrill(holder.assignment_id);
      onWithdrawn(holder.player_id, holder.assignment_id);
    } catch {
      toast.error("Could not withdraw that drill. Refresh and try again.");
    }
    setWithdrawing(null);
  };

  const noPlayers = roster.length === 0;
  const holderNames = holders.map((h) => h.full_name);

  return (
    <SettingsSheet
      open
      onOpenChange={(next) => { if (!next) onClose(); }}
      title={`Assign ${drill.title}`}
      description={noPlayers ? `No linked players to assign this drill to yet. Invite one from ${coachPlayersName()}.` : "Your players see it on Training & guides, with your name on it."}
      data-testid="drill-assign-sheet"
    >
      {!noPlayers && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {roster.map((p) => {
            const holder = holderByPlayer.get(p.player_id);
            if (holder) {
              return (
                <div key={p.player_id} data-testid={`drill-assign-row-${p.player_id}`} style={{ ...ROW, border: "1px solid var(--nw-line)" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, flexGrow: 1, minWidth: 0 }}>
                    <span style={NAME}>{p.full_name}</span>
                    <span style={CHIP}>Has it</span>
                  </span>
                  <button
                    type="button"
                    style={WITHDRAW}
                    disabled={withdrawing !== null}
                    aria-label={`Withdraw from ${p.full_name}`}
                    data-testid={`drill-assign-withdraw-${p.player_id}`}
                    onClick={() => withdraw(holder)}
                  >
                    Withdraw
                  </button>
                </div>
              );
            }
            const on = ticks.has(p.player_id);
            return (
              <div key={p.player_id} data-testid={`drill-assign-row-${p.player_id}`} style={{ ...ROW, gap: 4, border: `1px solid ${on ? "var(--nw-navy)" : "var(--nw-line)"}` }}>
                <span style={{ display: "flex", alignItems: "center", flexGrow: 1, minWidth: 0 }}>
                  <span style={NAME}>{p.full_name}</span>
                </span>
                <Tick checked={on} name={p.full_name} testId={`drill-assign-tick-${p.player_id}`} onToggle={() => toggle(p.player_id)} />
              </div>
            );
          })}
        </div>
      )}
      {holderNames.length > 0 && (
        <div className="nw-help" data-testid="drill-assign-holder-note">
          {`${joinNames(holderNames)} already ${holderNames.length === 1 ? "has" : "have"} this drill. Withdraw takes it back from them, as it does on their page today.`}
        </div>
      )}
      {partial && (
        <>
          <div
            role="alert"
            data-testid="drill-assign-failed"
            style={{
              display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)",
              borderLeftWidth: 3, borderLeftColor: "var(--nw-alert)", borderRadius: 10,
            }}
          >
            <TriangleAlert size={16} aria-hidden="true" style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 2 }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, color: "var(--nw-ink)" }}>{`Couldn't assign to ${joinNames(partial.failed)}.`}</div>
              <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>They're still ticked. Press Assign to try again.</div>
            </div>
          </div>
          {/* The board draws one success; two or more beside a failure are not drawn (S6), so no line. */}
          {partial.now.length === 1 && (
            <div className="nw-help" data-testid="drill-assign-now">{`${partial.now[0]} has it now.`}</div>
          )}
        </>
      )}
      {!noPlayers && (
        <ActionButton onClick={submit} disabled={ticked.length === 0 || busy} data-testid="drill-assign-submit">
          {ticked.length === 0 ? "Assign" : label}
        </ActionButton>
      )}
    </SettingsSheet>
  );
}
